import { useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, Trash2, AlertTriangle } from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { DataTable } from '@/components/DataTable'
import { AsyncBoundary } from '@/components/AsyncBoundary'
import { TerritoryPicker } from '@/components/TerritoryPicker'
import { BrokerCoverageSection } from '@/pages/BrokerCoveragePage'

interface BrokerRow {
  id: string
  name: string
  company?: string | null
  email: string
  phone?: string | null
  tier: string
  territory: string
  parentId?: string | null
  sentimentScore?: number
  sentimentLabel?: 'healthy' | 'good' | 'monitor' | 'at_risk' | 'urgent'
}

interface BrokerAlert {
  severity: 'red' | 'amber' | 'green'
  brokerId: string
  brokerName: string
  type: string
  message: string
  createdAt: string
}

const BROKER_TIERS = [
  { value: 'primary', label: 'Primary Broker', description: 'Main booking agent — direct relationship with artist' },
  { value: 'secondary', label: 'Secondary Broker', description: 'Works under primary, handles overflow or specific markets' },
  { value: 'sub_broker', label: 'Sub Broker', description: 'Regional sub-agent working under a primary or secondary' },
  { value: 'independent', label: 'Independent Broker', description: 'Freelance agent, not in the hierarchy' },
  { value: 'regional', label: 'Regional Agent', description: 'Territory-specific agent' },
  { value: 'specialist', label: 'Specialist', description: 'Niche market specialist (corporate, casino, festival)' },
  { value: 'co_promoter', label: 'Co-Promoter', description: 'Acts as both promoter and agent' },
  { value: 'exclusive', label: 'Exclusive Agent', description: 'Exclusive agent for a territory — all bookings go through them' },
] as const

const TERRITORIES = [
  'north_america', 'south_america', 'uk_ireland', 'europe_west',
  'europe_east', 'middle_east', 'africa', 'asia_pacific',
  'australia_nz', 'japan', 'india', 'global',
]

const SENTIMENT_COLOUR: Record<string, string> = {
  healthy: 'text-emerald-700 bg-emerald-50',
  good: 'text-emerald-700 bg-emerald-50',
  monitor: 'text-amber-700 bg-amber-50',
  at_risk: 'text-orange-700 bg-orange-50',
  urgent: 'text-red-700 bg-red-50',
}

const TIER_COLOUR: Record<string, string> = {
  primary: 'bg-blue-100 text-blue-800',
  secondary: 'bg-indigo-100 text-indigo-800',
  sub_broker: 'bg-violet-100 text-violet-800',
  independent: 'bg-slate-100 text-slate-800',
  regional: 'bg-teal-100 text-teal-800',
  specialist: 'bg-amber-100 text-amber-800',
  co_promoter: 'bg-pink-100 text-pink-800',
  exclusive: 'bg-emerald-100 text-emerald-800',
}

export function BrokersSection() {
  const qc = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [focusedBrokerId, setFocusedBrokerId] = useState<string | null>(null)

  const { data, error, isLoading } = useQuery<BrokerRow[], ApiClientError>({
    queryKey: ['brokers'],
    queryFn: () => artistFetch<BrokerRow[]>('/brokers'),
  })

  const { data: alerts } = useQuery<BrokerAlert[], ApiClientError>({
    queryKey: ['broker-alerts'],
    queryFn: () => artistFetch<BrokerAlert[]>('/brokers/alerts'),
  })

  async function handleDelete(broker: BrokerRow) {
    if (!window.confirm(`Delete broker "${broker.name}"? This is a soft delete and can be restored from the database.`)) return
    try {
      await artistFetch(`/brokers/${broker.id}`, { method: 'DELETE' })
      qc.invalidateQueries({ queryKey: ['brokers'] })
    } catch (err) {
      window.alert(err instanceof ApiClientError ? err.message : 'Failed to delete broker')
    }
  }

  const editingBroker = data?.find((b) => b.id === editingId) ?? null

  return (
    <>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-lg">Ticket agents & coverage</h2>
          <p className="text-sm text-ovation-muted">
            Primary and sub ticket agents, territory assignments, selling activity, and fan-demand gaps.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setAdding((a) => !a)}
          className="flex items-center gap-2 rounded-md bg-ovation-ink px-3 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90"
        >
          <Plus className="h-4 w-4" />
          {adding ? 'Cancel' : 'Add broker'}
        </button>
      </div>

      {(alerts?.length ?? 0) > 0 && (
        <div className="mb-6 space-y-2">
          {alerts!.map((alert) => (
            <div
              key={`${alert.brokerId}-${alert.type}-${alert.createdAt}`}
              className="flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <span className="font-medium">{alert.brokerName}</span>
                <span className="text-rose-800"> — {alert.message}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {adding && (
        <AddBrokerForm
          existingBrokers={data ?? []}
          onCreated={() => {
            setAdding(false)
            qc.invalidateQueries({ queryKey: ['brokers'] })
          }}
        />
      )}

      {editingBroker && (
        <EditBrokerForm
          broker={editingBroker}
          existingBrokers={data ?? []}
          onSaved={() => {
            setEditingId(null)
            qc.invalidateQueries({ queryKey: ['brokers'] })
          }}
          onCancel={() => setEditingId(null)}
        />
      )}

      <AsyncBoundary isLoading={isLoading} error={error}>
        <DataTable
          rows={data ?? []}
          rowKey={(r) => r.id}
          emptyMessage="No brokers added yet. Add your first booking agent above."
          onRowClick={(r) => setFocusedBrokerId((cur) => (cur === r.id ? null : r.id))}
          isRowSelected={(r) => r.id === focusedBrokerId}
          columns={[
            { key: 'name', header: 'Broker', render: (r) => (
              <div>
                <div className="font-medium">{r.company || r.name}</div>
                {r.company && <div className="text-xs text-ovation-muted">{r.name}</div>}
              </div>
            )},
            { key: 'tier', header: 'Level', render: (r) => (
              <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${TIER_COLOUR[r.tier] ?? 'bg-slate-100 text-slate-800'}`}>
                {BROKER_TIERS.find((t) => t.value === r.tier)?.label ?? r.tier.replace(/_/g, ' ')}
              </span>
            )},
            { key: 'email', header: 'Contact', render: (r) => (
              <div className="text-sm">
                <div>{r.email}</div>
                {r.phone && <div className="text-xs text-ovation-muted">{r.phone}</div>}
              </div>
            )},
            {
              key: 'territory',
              header: 'Territory',
              render: (r) => <span className="capitalize text-sm">{r.territory.replace(/_/g, ' ')}</span>,
            },
            {
              key: 'sentiment',
              header: 'Sentiment',
              render: (r) =>
                r.sentimentLabel ? (
                  <span className={`inline-flex rounded px-2 py-0.5 text-xs ${SENTIMENT_COLOUR[r.sentimentLabel] ?? ''}`}>
                    {r.sentimentLabel.replace('_', ' ')} {r.sentimentScore ?? ''}
                  </span>
                ) : (
                  <span className="text-xs text-ovation-muted">No data</span>
                ),
            },
            {
              key: 'actions',
              header: '',
              render: (r) => (
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => { setEditingId(r.id); setAdding(false) }}
                    className="rounded p-1.5 text-ovation-muted hover:bg-ovation-ink/5 hover:text-ovation-ink"
                    title="Edit broker"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(r)}
                    className="rounded p-1.5 text-ovation-muted hover:bg-red-50 hover:text-red-700"
                    title="Delete broker"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ),
            },
          ]}
        />
      </AsyncBoundary>

      <BrokerCoverageSection
        focusedBrokerId={focusedBrokerId}
        onClearFocus={() => setFocusedBrokerId(null)}
        onFocusBroker={(id) => setFocusedBrokerId((cur) => (cur === id ? null : id))}
      />
    </>
  )
}

/** @deprecated Use /marketing?tab=brokers — kept for old bookmarks. */
export function BrokersPage() {
  return <BrokersSection />
}

function AddBrokerForm({
  existingBrokers,
  onCreated,
}: {
  existingBrokers: BrokerRow[]
  onCreated: () => void
}) {
  const [name, setName] = useState('')
  const [company, setCompany] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [tier, setTier] = useState('primary')
  const [territory, setTerritory] = useState('uk_ireland')
  const [parentId, setParentId] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const showParent = ['secondary', 'sub_broker'].includes(tier)
  const primaryBrokers = existingBrokers.filter((b) => b.tier === 'primary' || b.tier === 'exclusive')

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await artistFetch('/brokers', {
        method: 'POST',
        body: {
          name: name.trim(),
          company: company.trim() || undefined,
          email: email.trim().toLowerCase(),
          phone: phone.trim() || undefined,
          tier,
          territory,
          parentId: showParent && parentId ? parentId : undefined,
        },
      })
      onCreated()
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={onSubmit} className="mb-6 space-y-4 rounded-lg border border-ovation-ink/10 bg-white p-6">
      <h3 className="font-display text-lg">Add a broker</h3>

      {/* Tier selection with descriptions */}
      <div>
        <label className="mb-2 block text-xs uppercase tracking-wider text-ovation-muted">Broker level</label>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {BROKER_TIERS.map((t) => (
            <label
              key={t.value}
              className={`flex cursor-pointer flex-col rounded-md border p-3 transition-colors ${
                tier === t.value
                  ? 'border-ovation-ink bg-ovation-ink/5'
                  : 'border-ovation-ink/10 hover:border-ovation-ink/30'
              }`}
            >
              <input
                type="radio"
                name="tier"
                value={t.value}
                checked={tier === t.value}
                onChange={() => setTier(t.value)}
                className="sr-only"
              />
              <span className="text-sm font-medium">{t.label}</span>
              <span className="mt-0.5 text-[10px] leading-tight text-ovation-muted">{t.description}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Parent broker (for secondary/sub) */}
      {showParent && primaryBrokers.length > 0 && (
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Reports to</label>
          <select
            value={parentId}
            onChange={(e) => setParentId(e.target.value)}
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
          >
            <option value="">— no parent —</option>
            {primaryBrokers.map((b) => (
              <option key={b.id} value={b.id}>{b.name} ({b.tier})</option>
            ))}
          </select>
        </div>
      )}

      {/* Contact details */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Name *</label>
          <input required value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" placeholder="John Smith" />
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Company</label>
          <input value={company} onChange={(e) => setCompany(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" placeholder="CAA, WME, etc." />
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Email *</label>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" placeholder="agent@agency.com" />
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Phone</label>
          <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" placeholder="+44 7xxx" />
        </div>
      </div>

      {/* Territory */}
      <div>
        <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Territory</label>
        <select
          value={territory}
          onChange={(e) => setTerritory(e.target.value)}
          className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
        >
          {TERRITORIES.map((t) => (
            <option key={t} value={t}>{t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</option>
          ))}
        </select>
      </div>

      {error && <div className="text-sm text-red-700">{error}</div>}
      <button type="submit" disabled={submitting} className="rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50">
        {submitting ? 'Adding...' : 'Add broker'}
      </button>
    </form>
  )
}

function EditBrokerForm({
  broker,
  existingBrokers,
  onSaved,
  onCancel,
}: {
  broker: BrokerRow
  existingBrokers: BrokerRow[]
  onSaved: () => void
  onCancel: () => void
}) {
  const [name, setName] = useState(broker.name)
  const [company, setCompany] = useState(broker.company ?? '')
  const [email, setEmail] = useState(broker.email)
  const [phone, setPhone] = useState(broker.phone ?? '')
  const [tier, setTier] = useState(broker.tier)
  const [territory, setTerritory] = useState(broker.territory)
  const [parentId, setParentId] = useState(broker.parentId ?? '')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const showParent = ['secondary', 'sub_broker'].includes(tier)
  const primaryBrokers = existingBrokers.filter(
    (b) => (b.tier === 'primary' || b.tier === 'exclusive') && b.id !== broker.id,
  )

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await artistFetch(`/brokers/${broker.id}`, {
        method: 'PATCH',
        body: {
          name: name.trim(),
          company: company.trim() || null,
          email: email.trim().toLowerCase(),
          phone: phone.trim() || null,
          tier,
          territory,
          parentId: showParent ? (parentId || null) : null,
        },
      })
      onSaved()
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={onSubmit} className="mb-6 space-y-4 rounded-lg border border-ovation-ink/10 bg-white p-6">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg">Edit broker</h3>
        <button type="button" onClick={onCancel} className="text-sm text-ovation-muted hover:text-ovation-ink">
          Cancel
        </button>
      </div>

      <div>
        <label className="mb-2 block text-xs uppercase tracking-wider text-ovation-muted">Broker level</label>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {BROKER_TIERS.map((t) => (
            <label
              key={t.value}
              className={`flex cursor-pointer flex-col rounded-md border p-3 transition-colors ${
                tier === t.value
                  ? 'border-ovation-ink bg-ovation-ink/5'
                  : 'border-ovation-ink/10 hover:border-ovation-ink/30'
              }`}
            >
              <input
                type="radio"
                name="edit-tier"
                value={t.value}
                checked={tier === t.value}
                onChange={() => setTier(t.value)}
                className="sr-only"
              />
              <span className="text-sm font-medium">{t.label}</span>
              <span className="mt-0.5 text-[10px] leading-tight text-ovation-muted">{t.description}</span>
            </label>
          ))}
        </div>
      </div>

      {showParent && primaryBrokers.length > 0 && (
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Reports to</label>
          <select
            value={parentId}
            onChange={(e) => setParentId(e.target.value)}
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
          >
            <option value="">— no parent —</option>
            {primaryBrokers.map((b) => (
              <option key={b.id} value={b.id}>{b.name} ({b.tier})</option>
            ))}
          </select>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Name *</label>
          <input required value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Company</label>
          <input value={company} onChange={(e) => setCompany(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Email *</label>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Phone</label>
          <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" />
        </div>
      </div>

      <div>
        <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Territory</label>
        <select
          value={territory}
          onChange={(e) => setTerritory(e.target.value)}
          className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
        >
          {TERRITORIES.map((t) => (
            <option key={t} value={t}>{t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</option>
          ))}
        </select>
      </div>

      {error && <div className="text-sm text-red-700">{error}</div>}
      <div className="flex gap-2">
        <button type="submit" disabled={submitting} className="rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50">
          {submitting ? 'Saving...' : 'Save changes'}
        </button>
        <button type="button" onClick={onCancel} className="rounded-md border border-ovation-ink/15 px-4 py-2 text-sm text-ovation-ink hover:bg-ovation-ink/5">
          Cancel
        </button>
      </div>

      <div className="mt-6 border-t border-ovation-ink/10 pt-6">
        <TerritoryPicker brokerId={broker.id} />
      </div>
    </form>
  )
}
